import React, {
  useId,
  useState,
  useCallback,
  useEffect,
  useContext
} from "react"
import type { ReactNode, ForwardedRef } from "react"
import classNames from "classnames"

import { ActionButton } from "./action-button"
import type {
  ActionProps,
  ActionItem,
  ActionContextType,
  ActionElement
} from "./types"

export type { ActionProps }

export const ActionContext = React.createContext<ActionContextType>({
  actions: [],
  register: () => {},
  remove: () => {}
})

type ActionProviderProps = {
  children: ReactNode
}

export const ActionProvider = ({ children }: ActionProviderProps) => {
  const [actions, setActions] = useState<ActionItem[]>([])

  const remove = useCallback((id: string) => {
    setActions(actions => actions.filter(action => action.id !== id))
  }, [])

  const register = useCallback(
    (id: string, props: ActionProps, ref: ForwardedRef<ActionElement>) => {
      setActions(actions => {
        let index = actions.findIndex(action => action.id === id)

        if (index == -1) index = actions.length

        return Object.assign([], actions, {
          [index]: {
            ...actions[index],
            ...props,
            ref,
            id
          }
        })
      })
    },
    []
  )

  return (
    <ActionContext.Provider value={{ actions, register, remove }}>
      {children}
    </ActionContext.Provider>
  )
}

const useAction = (
  id: string,
  props: ActionProps,
  ref: ForwardedRef<ActionElement>
) => {
  const { register, remove } = useContext(ActionContext)

  useEffect(() => {
    if (!id) return
    register(id, props, ref)
    return () => remove(id)
  }, [id, props, ref, register, remove])
}

// `min-w-0` keeps an unbreakable label wider than the footer from widening
// the whole group and pushing its siblings out of the modal.
const ACTION_GROUP_CLASSES = "flex flex-wrap items-center gap-4 min-w-0"

const ModalActionButton = ({ action }: { action: ActionItem }) => (
  <ActionButton action={action} className="shrink" />
)

export const Actions = () => {
  const { actions } = useContext(ActionContext)

  if (actions.length === 0) return null

  const leftActions = actions.filter(a => a.position === "left")
  const rightActions = actions.filter(a => a.position !== "left")
  const hasLeftActions = leftActions.length > 0

  return (
    <div
      className={classNames(
        "flex rounded-b-lg border-t border-gray-200 py-6 px-6 sticky bottom-0 bg-white",
        hasLeftActions ? "justify-between" : "justify-end"
      )}
      data-testid="actions"
    >
      {hasLeftActions && (
        <div className={ACTION_GROUP_CLASSES}>
          {leftActions.map(action => (
            <ModalActionButton key={action.id} action={action} />
          ))}
        </div>
      )}
      <div className={ACTION_GROUP_CLASSES}>
        {rightActions.map(action => (
          <ModalActionButton key={action.id} action={action} />
        ))}
      </div>
    </div>
  )
}

const Action = React.forwardRef<ActionElement, ActionProps>((props, ref) => {
  const id = useId()
  useAction(id, props, ref)

  return null
})

export default Action
